import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, AlertCircle, AlertTriangle, Info, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cantidadDe } from '@/lib/utils/formato-precio';
import { alertasService } from '@/features/dynamic-pricing/services/alertas-service';
import type { ResumenDeAlertas } from '@/features/dynamic-pricing/types/alertas.types';

/**
 * L5 (vault 278): el mismo modelo de alertas que Precios Dinámicos, para la
 * persona que mira el Dashboard. Antes no estaba montado y leía las alertas viejas.
 */
const ICONO = { CRITICAL: AlertCircle, WARNING: AlertTriangle, INFO: Info } as const;
const COLOR = { CRITICAL: 'text-destructive', WARNING: 'text-alerta', INFO: 'text-info' } as const;

export function MarketAlertsWidget() {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState<ResumenDeAlertas | null>(null);
  const [fallo, setFallo] = useState(false);

  const cargar = () => {
    setFallo(false);
    alertasService
      .resumen()
      .then(setResumen)
      .catch(() => setFallo(true));
  };

  useEffect(cargar, []);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShoppingCart className="h-4 w-4 text-primary" />
          Precios Dinámicos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {fallo && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">No se pudieron cargar las alertas.</p>
            <Button variant="outline" size="sm" onClick={cargar}>
              Reintentar
            </Button>
          </div>
        )}
        {resumen && resumen.noVistas === 0 && (
          <p className="text-sm text-muted-foreground">Sin alertas nuevas</p>
        )}
        {resumen && resumen.noVistas > 0 && (
          <>
            <p className="text-sm font-medium">
              {cantidadDe(resumen.noVistas, 'alerta sin ver', 'alertas sin ver')}
            </p>
            <div className="space-y-2">
              {resumen.ultimas.map((a) => {
                const Icono = ICONO[a.severity] ?? Info;
                return (
                  <div key={a.id} className="flex items-center gap-2 text-sm">
                    <Icono className={`h-3.5 w-3.5 shrink-0 ${COLOR[a.severity] ?? ''}`} />
                    <span className="truncate">{a.title}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs"
          onClick={() => navigate('/dynamic-pricing')}
        >
          Ver precios dinámicos
          <ArrowRight className="ml-1 h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
}
